import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Receipt,
  ShoppingBag,
  Truck,
} from "lucide-react";
import { FilterChipRow, Select } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { clientDetailPath } from "../routes";
import { StatsStrip, type StatTileItem } from "../components/data/StatsStrip";
import { DailyBars, DailyBarsCaption } from "../components/data/DailyBars";
import { OverviewDateControl } from "../components/dashboard/OverviewDateControl";
import { ClientBreakdownTable } from "../components/analytics/ClientBreakdownTable";
import { DriverBreakdownTable } from "../components/analytics/DriverBreakdownTable";
import { WalletSettlementsCard } from "../components/analytics/WalletSettlementsCard";
import {
  usePartnerAnalytics,
  usePartnerAnalyticsMoney,
  usePartnerClientsOverview,
} from "../hooks/usePartnerReports";
import { usePartnerDashboardDateRange } from "../hooks/usePartnerDashboardDateRange";
import { rangeToInstants } from "../lib/partnerReports";
import { useI18n } from "../i18n";

type FulfillmentFilter = "all" | "delivery" | "pickup";

export default function AnalyticsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { range, setToday, setCustom } = usePartnerDashboardDateRange("last30");
  const rangeInitRef = useRef(false);

  const tenantFilter = searchParams.get("tenant") ?? "all";
  const fulfillment = (searchParams.get("fulfillment") as FulfillmentFilter) ?? "all";
  const validFulfillment: FulfillmentFilter | null =
    fulfillment === "delivery" || fulfillment === "pickup" ? fulfillment : null;

  // Range init from URL (deep links from client reports).
  useEffect(() => {
    if (rangeInitRef.current) return;
    rangeInitRef.current = true;
    const fromRaw = searchParams.get("from");
    const toRaw = searchParams.get("to");
    if (!fromRaw || !toRaw) return;
    const from = new Date(fromRaw);
    const to = new Date(toRaw);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
    setCustom(from, to);
  }, [searchParams, setCustom]);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const syncRangeParams = (start: Date, end: Date) => {
    updateParams({ from: start.toISOString(), to: end.toISOString() });
  };

  const clientsQuery = usePartnerClientsOverview();
  const analyticsQuery = usePartnerAnalytics(range, {
    tenantId: tenantFilter === "all" ? null : tenantFilter,
    fulfillment: validFulfillment,
  });
  // 170: money layer (driver costs, OELOX charges, profit) with same filters.
  const moneyQuery = usePartnerAnalyticsMoney(range, {
    tenantId: tenantFilter === "all" ? null : tenantFilter,
    fulfillment: validFulfillment,
  });
  const moneyData = moneyQuery.data;
  const costById = useMemo(
    () =>
      new Map(
        (moneyData?.by_driver ?? []).map((d) => [d.driver_id, d.driver_cost] as const)
      ),
    [moneyData]
  );

  const data = analyticsQuery.data;
  const kpis = data?.kpis;
  const series = data?.series ?? [];
  const byClient = data?.by_client ?? [];
  const byDriver = data?.by_driver ?? [];
  const clients = clientsQuery.data ?? [];
  const firstDay = series.length > 0 ? series[0]?.day : undefined;
  const lastDay = series.length > 0 ? series[series.length - 1]?.day : undefined;

  const money = (v: number) => formatCurrency(v, "IQD", locale);

  const clientOptions = useMemo(
    () => [
      { value: "all", label: t("analytics.allClients") },
      ...clients.map((c) => ({ value: c.id, label: c.name })),
    ],
    [clients, t]
  );

  const fulfillmentOptions = [
    { key: "all", label: t("analytics.all") },
    { key: "delivery", label: t("common.delivery") },
    { key: "pickup", label: t("common.pickup") },
  ];

  const tiles: StatTileItem[] = [
    {
      key: "orders",
      label: t("analytics.kpiOrders"),
      value: kpis?.orders_total ?? "—",
      icon: ShoppingBag,
      tone: "accent",
      caption:
        kpis != null
          ? `${kpis.orders_completed} ${t("orders.stages.completed")} · ${kpis.orders_cancelled} ${t("orders.stages.cancelled")}`
          : undefined,
    },
    {
      key: "income",
      label: t("analytics.kpiDeliveryIncome"),
      value: kpis ? money(kpis.delivery_income) : "—",
      icon: Truck,
      tone: "info",
      caption: kpis
        ? `${t("analytics.fromCustomers")} ${money(kpis.delivery_income_customer)} · ${t("analytics.fromClients")} ${money(kpis.delivery_income_client)}`
        : undefined,
    },
    {
      key: "charges",
      label: t("analytics.kpiClientCharges"),
      value: kpis ? money(kpis.client_charges) : "—",
      icon: Receipt,
      tone: "success",
    },
    {
      key: "outstanding",
      label: t("analytics.kpiOutstanding"),
      value: kpis ? money(kpis.outstanding_pending) : "—",
      icon: AlertCircle,
      tone: "ink",
      caption: kpis
        ? t("analytics.pendingAdded", { amount: money(kpis.pending_added_in_range) })
        : undefined,
    },
  ];

  return (
    <div className="menu-page">
      <header className="menu-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("analytics.eyebrow")}</p>
          <h1 className="menu-page__title">{t("analytics.title")}</h1>
          <p className="menu-page__subtitle">{t("analytics.subtitle")}</p>
        </div>
        <div className="menu-page__header-actions">
          <OverviewDateControl
            range={range}
            onToday={() => {
              setToday();
              const now = new Date();
              const start = new Date(now);
              start.setHours(0, 0, 0, 0);
              const end = new Date(now);
              end.setHours(23, 59, 59, 999);
              syncRangeParams(start, end);
            }}
            onCustom={(start, end) => {
              setCustom(start, end);
              const s = new Date(start);
              s.setHours(0, 0, 0, 0);
              const e = new Date(end);
              e.setHours(23, 59, 59, 999);
              syncRangeParams(s, e);
            }}
            compact
            showLiveBadge={false}
          />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
          {t("analytics.colClient")}
          <Select
            value={tenantFilter}
            onChange={(e) =>
              updateParams({ tenant: e.target.value === "all" ? null : e.target.value })
            }
            options={clientOptions}
            aria-label={t("analytics.clientFilter")}
            className="min-w-[11rem]"
          />
        </label>
        <FilterChipRow
          options={fulfillmentOptions}
          value={fulfillment === "all" ? "all" : validFulfillment ?? "all"}
          onChange={(key) => updateParams({ fulfillment: key === "all" ? null : key })}
          ariaLabel={t("analytics.fulfillmentFilter")}
        />
      </div>

      {analyticsQuery.isError ? (
        <div className="rounded-xl border border-[var(--danger,#dc2626)]/30 bg-[var(--surface-muted)] px-4 py-3 text-sm text-[var(--danger,#dc2626)]">
          {t("analytics.loadError")}
        </div>
      ) : null}

      <StatsStrip tiles={tiles} loading={analyticsQuery.isLoading && !data} />

      {kpis ? (
        <p className="text-xs text-[var(--text-muted)]">
          {[
            `${t("analytics.kpiAvgFee")}: ${
              kpis.avg_delivery_fee != null ? money(kpis.avg_delivery_fee) : "—"
            }`,
            kpis.avg_delivery_km != null
              ? `${t("analytics.avgKm")}: ${kpis.avg_delivery_km}`
              : null,
            kpis.avg_prep_minutes != null
              ? `${t("analytics.avgPrep")}: ${kpis.avg_prep_minutes}`
              : null,
            `${t("analytics.kpiCod")}: ${money(kpis.cod_total)}`,
            `${t("analytics.kpiWallet")}: ${money(kpis.wallet_balance)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}

      {kpis && kpis.legacy_unpriced_count > 0 ? (
        <p className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text-muted)]">
          {t("clientView.legacyNote", { count: kpis.legacy_unpriced_count })}
        </p>
      ) : null}

      {kpis && kpis.cancelled_with_fee_count > 0 ? (
        <p className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text-muted)]">
          {t("analytics.cancelledWithFee", { count: kpis.cancelled_with_fee_count })}
        </p>
      ) : null}

      {moneyData ? (
        <section className="menu-panel">
          <div className="menu-panel__body space-y-3">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.profitabilityTitle")}
            </h3>
            <dl className="divide-y divide-[var(--border-subtle)] text-sm">
              <div className="flex items-center justify-between gap-4 py-2">
                <dt className="text-[var(--text-secondary)]">
                  {t("analytics.profitRevenue")}
                </dt>
                <dd className="tabular font-bold text-[var(--text-primary)]">
                  {money(moneyData.revenue_estimate)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <dt className="text-[var(--text-secondary)]">
                  {t("analytics.profitDriverCosts")}
                </dt>
                <dd className="tabular font-bold text-[var(--text-primary)]">
                  −{money(moneyData.driver_cost)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <dt className="text-[var(--text-secondary)]">
                  {t("analytics.profitOelox")}
                </dt>
                <dd className="tabular font-bold text-[var(--text-primary)]">
                  −{money(moneyData.oelox_cost)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <dt className="font-bold text-[var(--text-primary)]">
                  {t("analytics.profitEstimated")}
                </dt>
                <dd
                  className={`tabular text-base font-black ${
                    moneyData.profit_estimate < 0
                      ? "text-red-600"
                      : "text-[var(--text-primary)]"
                  }`}
                >
                  {money(moneyData.profit_estimate)}
                  {moneyData.margin_pct != null ? (
                    <span className="ms-2 text-xs font-semibold text-[var(--text-muted)]">
                      {t("analytics.profitMarginValue", { pct: moneyData.margin_pct })}
                    </span>
                  ) : null}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-[var(--text-muted)]">
              {[
                t("analytics.profitSettledTrips", { count: moneyData.settled_trips }),
                moneyData.voided_total > 0
                  ? t("analytics.profitVoided", {
                      amount: money(moneyData.voided_total),
                    })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            {!moneyData.has_rates ? (
              <p className="text-xs text-[var(--text-muted)]">
                {t("analytics.profitNoRates")}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="menu-panel">
          <div className="menu-panel__body">
            <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.ordersPerDay")}
            </h3>
            <DailyBars
              points={series.map((p) => ({ day: p.day, value: p.orders }))}
              ariaLabel={t("analytics.ordersPerDay")}
            />
            {firstDay && lastDay ? (
              <DailyBarsCaption first={firstDay} last={lastDay} />
            ) : null}
          </div>
        </section>
        <section className="menu-panel">
          <div className="menu-panel__body">
            <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.incomePerDay")}
            </h3>
            <DailyBars
              points={series.map((p) => ({ day: p.day, value: p.delivery_income }))}
              ariaLabel={t("analytics.incomePerDay")}
              tone="info"
              formatValue={money}
            />
            {firstDay && lastDay ? (
              <DailyBarsCaption first={firstDay} last={lastDay} />
            ) : null}
          </div>
        </section>
      </div>

      <section className="menu-panel">
        <div className="menu-panel__body">
          <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("analytics.byClient")}
          </h3>
          <ClientBreakdownTable
            rows={byClient}
            loading={analyticsQuery.isLoading && !data}
            onOpen={(tenantId) => {
              const { fromIso, toIso } = rangeToInstants(range);
              navigate(
                `${clientDetailPath(tenantId)}?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`
              );
            }}
          />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="menu-panel">
          <div className="menu-panel__body">
            <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.byDriver")}
            </h3>
            <DriverBreakdownTable
              rows={byDriver}
              loading={analyticsQuery.isLoading && !data}
              costById={costById}
            />
          </div>
        </section>
        <WalletSettlementsCard />
      </div>

      <p className="text-xs text-[var(--text-muted)]">{t("analytics.pendingNote")}</p>
    </div>
  );
}
